import React, { memo, useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import getSymbolFromCurrency from 'currency-symbol-map';
import Animated, { withTiming, interpolate, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import createStyleSheets from 'utils/createStyleSheets';
import { MoneyTextInput } from 'utils/packages/moneyInput';
import { isWeb } from '../../../../constants';
import CategoryMenuButton from './CategoryMenuButton';
const SplitItem = ({ amount, currency, category, id, onPressShowMenu, onChangeText, animate, maxAmount, }) => {
    const colors = useColors();
    const [text, setText] = useState(() => amount.toFixed(2));
    const [isFocused, setIsFocused] = useState(false);
    const opacity = useSharedValue(animate ? 0 : 1);
    const translateY = useSharedValue(animate ? 0 : 1);
    const animStyle = useAnimatedStyle(() => ({
        opacity: opacity.value,
        transform: [
            {
                translateY: interpolate(translateY.value, [0, 1], [-16, 0]),
            },
        ],
    }));
    useEffect(() => {
        opacity.value = withTiming(1, { duration: 250 });
        translateY.value = withTiming(1, { duration: 500 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        setText((current) => Number(current || 0) === amount ? current : amount.toFixed(2));
    }, [amount]);
    const changeText = useCallback((_formatted, extracted = '') => {
        setText(extracted);
        onChangeText(Number(extracted || 0), id);
    }, [id, onChangeText]);
    const isEditable = id !== 0;
    const styles = useStyles(styleSet);
    return (<Animated.View style={[styles.container, animStyle]}>
      <Text Text-14 Gray style={styles.header}>
        Transaction {id + 1}
      </Text>
      <View style={styles.content}>
        <CategoryMenuButton idx={id} selected={category} onPressShowMenu={onPressShowMenu}/>
        <View style={styles.flex}/>
        <View>
          <MoneyTextInput pointerEvents={isEditable ? 'auto' : 'none'} style={[
            styles.textinput,
            isFocused && styles.focused,
            !isEditable && styles.unEditable,
        ]} editable={isEditable} allowFontScaling={false} selectionColor={colors.text.selection} returnKeyType="done" returnKeyLabel="Done" underlineColorAndroid="transparent" prefix={getSymbolFromCurrency(currency)} onFocus={() => setIsFocused(isEditable)} onBlur={() => setIsFocused(false)} onChangeText={changeText} value={text} testID="splitInput" maxValue={isEditable && maxAmount ? Math.abs(maxAmount) - 0.01 : undefined} maximumIntegerDigits={maxAmount ? Math.abs(maxAmount).toString().length : undefined}/>
        </View>
      </View>
    </Animated.View>);
};
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    container: {
        overflow: 'hidden',
        padding: rem(16),
        transform: [{ translateY: -16 }],
        opacity: 0,
    },
    content: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    header: {
        paddingBottom: rem(16),
    },
    textinput: {
        fontSize: rem(16),
        fontFamily: 'Nunito-Bold',
        textAlign: 'right',
        margin: 0,
        ...(isWeb
            ? {
                width: rem(120),
                color: colors.text.primary,
                backgroundColor: colors.background.dark,
                borderWidth: rem(1),
                borderRadius: rem(8),
                paddingVertical: rem(8),
                paddingHorizontal: rem(16),
                borderColor: colors.borders.divider,
                caretColor: colors.text.selection,
            }
            : {
                padding: 0,
                borderBottomWidth: 1,
                color: colors.text.primary,
                borderBottomColor: colors.elements.secondary,
            }),
    },
    focused: isWeb ? { borderColor: colors.elements.brand } : {},
    unEditable: {
        ...(isWeb
            ? {
                color: colors.text.secondary,
            }
            : {
                borderBottomWidth: 0,
            }),
    },
}));
export default memo(SplitItem);
